export const getNextShowtimes = (showtimes, locale) => {
  if (!Array.isArray(showtimes)) {
    return [];
  }

  const now = Date.now();

  return showtimes
    .map((showtime) => {
      const startValue =
        typeof showtime === 'string'
          ? showtime
          : showtime?.startTime ?? showtime?.startDate ?? null;

      if (!startValue) {
        return null;
      }

      if (typeof startValue === 'string' && /^\d{1,2}:\d{2}(?:\s*-\s*\d{1,2}:\d{2})?$/.test(startValue)) {
        return {
          formattedTime: startValue,
          timestamp: Number.MAX_SAFE_INTEGER,
          endTimestamp: null
        };
      }

      const startDate = new Date(startValue);

      if (Number.isNaN(startDate.getTime())) {
        return null;
      }

      const endValue = typeof showtime !== 'string' ? (showtime?.endTime ?? null) : null;
      const endTimestamp = endValue ? new Date(endValue).getTime() : null;

      return {
        formattedTime: startDate.toLocaleTimeString(locale || undefined, {
          hour: '2-digit',
          minute: '2-digit'
        }),
        timestamp: startDate.getTime(),
        endTimestamp
      };
    })
    .filter(Boolean)
    // Keep showtimes that haven't started yet OR are currently running (endTime still in future)
    .filter((showtime) => showtime.timestamp > now || (showtime.endTimestamp != null && showtime.endTimestamp > now))
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((showtime) => showtime.formattedTime);
};

export const isShowMapEntity = (item) => {
  return item?.entityType === 'SHOW' || (Array.isArray(item?.showtimes) && item.showtimes.length > 0);
};

export const getMapEntityPrimaryLabel = (item, t) => {
  return isShowMapEntity(item)
    ? t('shows.schedule', 'Horaires')
    : t('attractions.waitTime', 'Temps d\'attente');
};

export const MAP_GEOLOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 12000,
  maximumAge: 60000
};

export const calculateDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  const earthRadius = 6371e3;
  const latitudeDelta = (lat2 - lat1) * Math.PI / 180;
  const longitudeDelta = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(latitudeDelta / 2) * Math.sin(latitudeDelta / 2) +
    Math.cos(lat1 * Math.PI / 180) *
      Math.cos(lat2 * Math.PI / 180) *
      Math.sin(longitudeDelta / 2) *
      Math.sin(longitudeDelta / 2);

  return earthRadius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

export const getMapGeolocationErrorMessage = (error, t) => {
  switch (error?.code) {
    case 1:
      return t('errors.geolocation.denied', 'L\'accès à votre position a été refusé.');
    case 2:
      return t('errors.geolocation.unavailable', 'Impossible d\'obtenir votre position.');
    case 3:
      return t('errors.geolocation.timeout', 'La demande de position a expiré.');
    default:
      return t('errors.geolocation.generic', 'Une erreur est survenue lors de la géolocalisation');
  }
};

const normalizeMapName = (value) => {
  if (!value) {
    return '';
  }

  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[®™]/g, '')
    .replace(/[\u2018\u2019\u201B\u2032\u2035\u0060\u00B4]/g, "'")
    .replace(/[“”«»]/g, '"')
    .replace(/–/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

export const resolveMapLocation = (item, locations) => {
  if (!item) {
    return null;
  }

  if (locations?.[item.name]) {
    return locations[item.name];
  }

  const normalizedName = normalizeMapName(item.name);

  if (normalizedName && locations) {
    const matchedEntry = Object.entries(locations).find(([key]) => {
      return normalizeMapName(key) === normalizedName;
    });

    if (matchedEntry) {
      return matchedEntry[1];
    }
  }

  if (item.location?.latitude != null && item.location?.longitude != null) {
    return {
      latitude: item.location.latitude,
      longitude: item.location.longitude
    };
  }

  if (item.latitude != null && item.longitude != null) {
    return {
      latitude: item.latitude,
      longitude: item.longitude
    };
  }

  return null;
};

export const normalizeMapEntity = (item, { locations, park, locale }) => {
  if (!item) {
    return null;
  }

  const location = resolveMapLocation(item, locations);

  if (!location) {
    return null;
  }

  const isShow = isShowMapEntity(item);

  return {
    id: item.id,
    name: item.name,
    entityType: isShow ? 'SHOW' : item.entityType || 'ATTRACTION',
    waitTime: item.waitTime ?? item.queue?.STANDBY?.waitTime ?? -1,
    singleRiderWaitTime:
      item.singleRiderWaitTime ?? item.queue?.SINGLE_RIDER?.waitTime ?? null,
    status: item.status,
    showtimes: isShow ? getNextShowtimes(item.showtimes, locale) : [],
    latitude: location.latitude,
    longitude: location.longitude,
    park
  };
};

export const filterVisibleMapEntities = (entities, showClosed) => {
  if (!Array.isArray(entities)) {
    return [];
  }

  if (showClosed) {
    return entities;
  }

  return entities.filter((entity) => {
    if (isShowMapEntity(entity)) {
      // Shows are fixed venues — always visible on the map regardless of remaining sessions.
      return true;
    }

    return entity.status !== 'CLOSED' && entity.status !== 'REFURBISHMENT';
  });
};
